import type { ProbabilityItem } from '../server/item-probabilities';
import { nestApiClient } from './api-client';

/**
 * Full tables for one item, fetched when its popup opens rather than shipped with the
 * list: a page of 20 grabbags carries megabytes of sections, and a reader opens one.
 */
export const getItemProbabilityById = async (
  id: string,
  locale: string
): Promise<ProbabilityItem> => {
  // locale travels as a query parameter, not the `locale` header the server-side callers
  // use: a custom header would make this a non-simple request, and the CORS preflight it
  // triggers is rejected by the edge in front of the deployed API, which allows only
  // `content-type`. As a plain GET this needs no preflight at all.
  const response = await nestApiClient.get(
    `/api/v1/item-probabilities/${encodeURIComponent(id)}`,
    { params: { locale } }
  );

  // The client interceptor hands back the raw body, which is the Nest envelope.
  return response.data.data;
};

/**
 * Whether this feature exists for the viewer, asked from the viewer's own browser.
 *
 * The navigation cannot answer this while rendering. The sidebar belongs to documents
 * that every reader shares in the CDN cache, so a country-dependent link baked
 * into that HTML would be either wrong for most readers or right only at the price of
 * putting the viewer country into the cache key of the whole site. Asking afterwards, from
 * the browser, keeps every cached document identical and leaves the caching untouched.
 *
 * The answer is the status code of the odds API's own guard — the same one that serves the
 * tables — so the link cannot appear for someone the page would then refuse.
 */
const AVAILABILITY_PATH = '/api/v1/item-probabilities/availability';

const AVAILABILITY_STORAGE_KEY = 'item-probabilities:available';

/** Session storage is absent or throws in private windows; the probe just runs again. */
const rememberedAvailability = (): boolean | undefined => {
  try {
    const stored = window.sessionStorage.getItem(AVAILABILITY_STORAGE_KEY);
    return stored == null ? undefined : stored === 'true';
  } catch {
    return undefined;
  }
};

const rememberAvailability = (available: boolean) => {
  try {
    window.sessionStorage.setItem(AVAILABILITY_STORAGE_KEY, String(available));
  } catch {
    // Nothing to do: without storage the answer is re-fetched, which is only a cost.
  }
};

/** Resolves for an answer the API actually gave; throws for anything inconclusive. */
const probeAvailability = async (): Promise<boolean> => {
  const remembered = rememberedAvailability();
  if (remembered !== undefined) return remembered;

  // 404 is the guard's answer, not a failure, so it must not reach the error interceptor:
  // that path exists to refresh tokens and classify real faults, and none of that applies
  // to a question whose answer is "no".
  const response = await nestApiClient.get(AVAILABILITY_PATH, {
    validateStatus: (status) => status === 200 || status === 404,
  });

  const available = response.status === 200;
  rememberAvailability(available);
  return available;
};

let availabilityProbe: Promise<boolean> | null = null;

export const isOddsPageAvailable = (): Promise<boolean> => {
  // One request per session, however many places ask: the desktop sidebar and the mobile
  // drawer render the same link, and client-side navigation keeps this module alive
  // across pages.
  availabilityProbe ??= probeAvailability().catch(() => {
    // A timeout, a 5xx or a dropped connection is not an answer. Hide the link for now —
    // but forget the attempt, so the next mount asks again rather than a single blip
    // costing a Korean reader the disclosure link for the rest of their session.
    availabilityProbe = null;
    return false;
  });

  return availabilityProbe;
};
